import moment from "moment-timezone";
import {
  formatChange,
  convertTo24HourFormat,
  convertTo12HourFormat,
  convertTo24HourFormatback,
  isEndTimeGreater,
  validateInput,
  convertTo24Hour,
  validateNoAlphabets,
} from "../services/timeFormat";
import {
  formatTime,
  timeValidate,
  isValidCustomTimeFormat,
  timeFormatChange,
  convertToStandardTime,
  timeFomatChange2,
  subOneHour,
} from "../services/trackerServices";

/**
 * Creates manual time change handlers with all necessary dependencies
 * @param {Object} dependencies - Object containing all required dependencies
 * @returns {Object} Object containing handler functions
 */
export function createManualTimeHandlers({
  // State setters
  setStartTimeManual,
  setEndTimeManual,
  setStartTimeManual24,
  setEndTimeManual24,
  setStartTimeTemp,
  setEndTimeTemp,
  setUDuration,
  setUFormatedTimeManual,
  setUManualStartTimeError,
  setUManualEndTimeError,
  setUManualFormatedTimeError,
  setSplitted,
  setSplittedDate,
  // State values
  endTimeManual,
  startTimeManual,
  splitted,
  splittedDate,
}) {
  /**
   * Handle manual start time change (12-hour format)
   */
  const manualChangeStartTime = (startTime) => {
    try {
      if (!startTime || typeof startTime !== "string") {
        console.error("manualChangeStartTime: Invalid input. Expected a non-empty string.");
        setUManualStartTimeError(true);
        return;
      }

      let formattedTime = timeFormatChange(startTime);

      if (formattedTime === false) {
        console.error("manualChangeStartTime: Invalid time format.", startTime);
        setUManualStartTimeError(true);
        return;
      }

      setStartTimeManual(formattedTime);
      setStartTimeTemp(formattedTime);
      setUManualStartTimeError(false);
      setUManualEndTimeError(false);

      const timeString1 = formattedTime.trim();
      const timeString2 = endTimeManual?.trim();

      if (!timeString2) {
        console.error("manualChangeStartTime: End time is not set.");
        setUManualStartTimeError(true);
        return;
      }

      const format = "h:mm A";
      const time1 = moment(timeString1, format).tz(global.config.getTimeZone());
      const time2 = moment(timeString2, format).tz(global.config.getTimeZone());

      if (!time1.isValid() || !time2.isValid()) {
        console.error("manualChangeStartTime: Invalid moment time objects.");
        setUManualStartTimeError(true);
        return;
      }

      const timeDifferenceInSeconds = time2.diff(time1, "seconds");

      if (timeDifferenceInSeconds > 0) {
        setUDuration(parseInt(timeDifferenceInSeconds));
        setUFormatedTimeManual(prev => {
          const newFormattedTime = `${formatTime(Math.floor(timeDifferenceInSeconds / 3600))}:${formatTime(
            Math.floor((timeDifferenceInSeconds % 3600) / 60)
          )}:${formatTime(timeDifferenceInSeconds % 60)}`;
          return newFormattedTime;
        });
        setUManualStartTimeError(false);
        setUManualEndTimeError(false);
        setUManualFormatedTimeError(false);
        // Clear splitted state when manually changing start time (single day time)
        setSplitted(false);
        setSplittedDate([]);
      } else {
        console.error("manualChangeStartTime: End time must be greater than start time.");
        setUManualStartTimeError(true);
      }
    } catch (error) {
      console.error("Error in manualChangeStartTime:", error);
      setUManualStartTimeError(true);
    }
  };

  /**
   * Handle manual start time change (24-hour format)
   */
  const manualChangeStartTime24 = (startTime) => {
    try {
      if (!startTime || typeof startTime !== "string") {
        console.error("manualChangeStartTime24: Invalid input. Expected non-empty string.");
        setUManualStartTimeError(true);
        return;
      }

      setStartTimeManual24(startTime);

      let convertedTim = null;
      if (formatChange(startTime)) {
        convertedTim = convertTo24HourFormat(startTime);
        setStartTimeManual24(convertedTim);
        setUManualStartTimeError(false);
      } else {
        console.error("manualChangeStartTime24: Invalid 24-hour format.");
        setUManualStartTimeError(true);
        return;
      }

      startTime = convertTo12HourFormat(convertedTim);
      
      // Normalize the time format (ensure AM/PM is uppercase with space)
      // Handle both "09:00am" and "09:00 am" formats
      if (startTime) {
        startTime = startTime.trim();
        // Replace "am"/"pm" (with or without space) with " AM"/" PM"
        startTime = startTime.replace(/\s*(am|pm)\s*$/i, (match, period) => {
          return ' ' + period.toUpperCase();
        });
      }
      
      // Only call timeFomatChange2 if the time matches its expected input pattern (e.g., "3:45p" or "11:30a")
      // timeFomatChange2 expects format like "3:45p" (single letter a/p, no space), not "09:00 AM"
      if (startTime) {
        const timePattern = /^\d{1,2}:\d{2}[ap]$/i;
        if (timePattern.test(startTime.trim())) {
          const converted = timeFomatChange2(startTime);
          if (converted !== false) {
            startTime = converted;
          }
        }
        // If time is already in standard format (e.g., "09:00 AM"), skip timeFomatChange2
      }

      if (!startTime) {
        console.error("manualChangeStartTime24: Failed to convert time.");
        setUManualStartTimeError(true);
        return;
      }

      if (isValidCustomTimeFormat(startTime)) {
        startTime = convertToStandardTime(startTime);
        setUManualStartTimeError(false);
        setUManualEndTimeError(false);
      } else {
        const timePart = startTime.split(" ")[0];
        let hours = parseInt(timePart.split(":")[0], 10);
        if (isNaN(hours)) {
          console.error("manualChangeStartTime24: Invalid hours in time.");
          setUManualStartTimeError(true);
          return;
        }

        if (hours > 12 && hours < 24) {
          const twelveHourTime = moment(timePart, "HH:mm").format("hh:mm A");
          if (twelveHourTime === "Invalid date") {
            console.error("manualChangeStartTime24: Failed to convert to 12-hour format.");
            setUManualStartTimeError(true);
            return;
          }
          startTime = twelveHourTime;
          setUManualStartTimeError(false);
          setUManualEndTimeError(false);
        }
      }

      if (timeValidate(startTime)) {
        const timeString1 = startTime.trim();
        const timeString2 = endTimeManual?.trim();

        if (!timeString2) {
          console.error("manualChangeStartTime24: End time is missing.");
          setUManualStartTimeError(true);
          return;
        }

        const format = "h:mm A";
        const time1 = moment(timeString1, format).tz(global.config.getTimeZone());
        const time2 = moment(timeString2, format).tz(global.config.getTimeZone());

        if (!time1.isValid() || !time2.isValid()) {
          console.error("manualChangeStartTime24: Invalid moment time objects.");
          setUManualStartTimeError(true);
          return;
        }

        // Check if splittedDate is an object with day1 and day2 properties (split time structure)
        const isSplitTime = splitted && splittedDate && (
          (Array.isArray(splittedDate) && splittedDate.length > 0) ||
          (typeof splittedDate === 'object' && splittedDate.day1 && splittedDate.day2)
        );

        const timeDifferenceInSeconds = time2.diff(time1, "seconds");

        // Skip validation for split times (times spanning multiple days)
        if (isSplitTime) {
          // For split times, don't validate end time > start time
          // The duration should already be set from uFormatedTimeManual
          setUManualStartTimeError(false);
          setUManualEndTimeError(false);
          setUManualFormatedTimeError(false);
        } else if (timeDifferenceInSeconds > 0) {
          setUDuration(parseInt(timeDifferenceInSeconds));
          setUFormatedTimeManual(prev => {
            const newFormattedTime = `${formatTime(Math.floor(timeDifferenceInSeconds / 3600))}:${formatTime(
              Math.floor((timeDifferenceInSeconds % 3600) / 60)
            )}:${formatTime(timeDifferenceInSeconds % 60)}`;
            return newFormattedTime;
          });
          setUManualStartTimeError(false);
          setUManualEndTimeError(false);
          setUManualFormatedTimeError(false);
          // Clear splitted state when manually changing start time (single day time)
          setSplitted(false);
          setSplittedDate([]);
        } else {
          console.error("manualChangeStartTime24: End time must be after start time.");
          setUManualStartTimeError(true);
        }
      } else {
        console.error("manualChangeStartTime24: Time validation failed.");
        setUManualStartTimeError(true);
      }

      setStartTimeManual(startTime);

    } catch (error) {
      console.error("Error in manualChangeStartTime24:", error);
      setUManualStartTimeError(true);
    }
  };

  /**
   * Handle manual end time change (12-hour format)
   */
  const manualChangeEndTime = (endTime) => {
    try {
      if (!endTime || typeof endTime !== "string") {
        console.error("manualChangeEndTime: Invalid input. Expected non-empty string.");
        setUManualEndTimeError(true);
        return;
      }

      let formattedTime = timeFormatChange(endTime);

      if (formattedTime === false) {
        console.error("manualChangeEndTime: Time format conversion failed.");
        setUManualEndTimeError(true);
        return;
      }

      setEndTimeManual(formattedTime);
      setEndTimeTemp(formattedTime);

      if (!startTimeManual || typeof startTimeManual !== "string") {
        console.error("manualChangeEndTime: startTimeManual is not set or invalid.");
        setUManualEndTimeError(true);
        return;
      }

      const timeString1 = startTimeManual.trim();
      const timeString2 = formattedTime.trim();
      const format = "h:mm A";

      const time1 = moment(timeString1, format).tz(global.config.getTimeZone());
      const time2 = moment(timeString2, format).tz(global.config.getTimeZone());

      if (!time1.isValid() || !time2.isValid()) {
        console.error("manualChangeEndTime: Invalid moment time objects.");
        setUManualEndTimeError(true);
        return;
      }

      const timeDifferenceInSeconds = time2.diff(time1, "seconds");

      if (timeDifferenceInSeconds > 0) {
        setUDuration(parseInt(timeDifferenceInSeconds));
        setUFormatedTimeManual(
          `${formatTime(Math.floor(timeDifferenceInSeconds / 3600))}:${formatTime(
            Math.floor((timeDifferenceInSeconds % 3600) / 60)
          )}:${formatTime(Math.floor(timeDifferenceInSeconds % 60))}`
        );
        setUManualStartTimeError(false);
        setUManualEndTimeError(false);
        setUManualFormatedTimeError(false);
        // Clear splitted state when manually changing end time (single day time)
        setSplitted(false);
        setSplittedDate([]);
      } else {
        console.error("manualChangeEndTime: End time must be after start time.");
        setUManualEndTimeError(true);
      }

    } catch (error) {
      console.error("Error in manualChangeEndTime:", error);
      setUManualEndTimeError(true);
    }
  };

  /**
   * Handle manual end time change (24-hour format)
   */
  const manualChangeEndTime24 = (endTime) => {
    try {
      if (!endTime || typeof endTime !== "string") {
        console.error("manualChangeEndTime24: Invalid endTime input.");
        setUManualEndTimeError(true);
        return;
      }

      setEndTimeManual24(endTime);

      let convertedTim;
      if (formatChange(endTime)) {
        convertedTim = convertTo24HourFormat(endTime);
        setEndTimeManual24(convertedTim);
        setUManualEndTimeError(false);
      } else {
        console.error("manualChangeEndTime24: Invalid time format.");
        setUManualEndTimeError(true);
        return;
      }

      if (!convertedTim) {
        console.error("manualChangeEndTime24: Conversion to 24-hour failed.");
        setUManualEndTimeError(true);
        return;
      }

      endTime = convertTo12HourFormat(convertedTim);

      // Normalize the time format (ensure AM/PM is uppercase with space)
      // Handle both "09:00am" and "09:00 am" formats
      if (endTime) {
        endTime = endTime.trim();
        // Replace "am"/"pm" (with or without space) with " AM"/" PM"
        endTime = endTime.replace(/\s*(am|pm)\s*$/i, (match, period) => {
          return ' ' + period.toUpperCase();
        });
      }

      // Only call timeFomatChange2 if the time matches its expected input pattern (e.g., "3:45p" or "11:30a")
      // timeFomatChange2 expects format like "3:45p" (single letter a/p, no space), not "09:00 AM"
      if (endTime) {
        const timePattern = /^\d{1,2}:\d{2}[ap]$/i;
        if (timePattern.test(endTime.trim())) {
          const converted = timeFomatChange2(endTime);
          if (converted !== false) {
            endTime = converted;
          }
        }
        // If time is already in standard format (e.g., "09:00 AM"), skip timeFomatChange2
      }

      const oldTime = isValidCustomTimeFormat(endTime) ? endTime : null;
      if (isValidCustomTimeFormat(endTime)) {
        endTime = convertToStandardTime(endTime);
      } else {
        const timePart = endTime.split(" ")[0];
        const hours = parseInt(timePart.split(":")[0], 10);
        if (hours > 12 && hours < 24) {
          const twelveHourTime = moment(timePart, "HH:mm").format("hh:mm A");
          if (twelveHourTime !== "Invalid date") {
            endTime = twelveHourTime;
          } else {
            console.error("manualChangeEndTime24: Invalid date in conversion.");
            setUManualEndTimeError(true);
            return;
          }
        }
      }

      if (timeValidate(endTime)) {
        if (!startTimeManual || typeof startTimeManual !== "string") {
          console.error("manualChangeEndTime24: startTimeManual is not set or invalid.");
          setUManualEndTimeError(true);
          return;
        }

        let timeString1 = startTimeManual.trim();
        const timeString2 = endTime.trim();
        const format = "h:mm A";

        if (isValidCustomTimeFormat(oldTime)) {
          timeString1 = subOneHour(endTime).trim();
          setStartTimeManual(timeString1);
        }

        const time1 = moment(timeString1, format).tz(global.config.getTimeZone());
        const time2 = moment(timeString2, format).tz(global.config.getTimeZone());

        if (!time1.isValid() || !time2.isValid()) {
          console.error("manualChangeEndTime24: Invalid moment time objects.");
          setUManualEndTimeError(true);
          return;
        }

        // Check if splittedDate is an object with day1 and day2 properties (split time structure)
        const isSplitTime = splitted && splittedDate && (
          (Array.isArray(splittedDate) && splittedDate.length > 0) ||
          (typeof splittedDate === 'object' && splittedDate.day1 && splittedDate.day2)
        );

        const timeDifferenceInSeconds = time2.diff(time1, "seconds");

        // Skip validation for split times (times spanning multiple days)
        if (isSplitTime) {
          // For split times, don't validate end time > start time
          // The duration should already be set from uFormatedTimeManual
          setUManualStartTimeError(false);
          setUManualEndTimeError(false);
          setUManualFormatedTimeError(false);
        } else if (timeDifferenceInSeconds > 0) {
          setUDuration(parseInt(timeDifferenceInSeconds));
          setUFormatedTimeManual(
            `${formatTime(Math.floor(timeDifferenceInSeconds / 3600))}:${formatTime(
              Math.floor((timeDifferenceInSeconds % 3600) / 60)
            )}:${formatTime(Math.floor(timeDifferenceInSeconds % 60))}`
          );
          setUManualStartTimeError(false);
          setUManualEndTimeError(false);
          setUManualFormatedTimeError(false);
          // Clear splitted state when manually changing end time (single day time)
          setSplitted(false);
          setSplittedDate([]);
        } else {
          console.error("manualChangeEndTime24: End time must be after start time.");
          setUManualEndTimeError(true);
        }
      } else {
        console.error("manualChangeEndTime24: Time validation failed.");
        setUManualEndTimeError(true);
      }

      setEndTimeManual(endTime);

    } catch (error) {
      console.error("Error in manualChangeEndTime24:", error);
      setUManualEndTimeError(true);
    }
  };

  return {
    manualChangeStartTime,
    manualChangeStartTime24,
    manualChangeEndTime,
    manualChangeEndTime24,
  };
}
